/**
 * Agents API layer
 * Handles fetching and managing agent data from the backend
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './base/client';
import { Agent, AgentPinResponse, AgentsResponse } from './base/types';
import { ShowPreviewFeatures } from '../config/frontendconfig';
import { ApiError } from './base/errors';
import { useAgentsStore } from '../stores/agentsStore';

/**
 * Custom error class for agent-specific errors
 */
export class AgentApiError extends ApiError {
  public readonly name: string = 'AgentApiError';

  constructor(message: string, status?: number, code?: string) {
    super(message, status, code);
    Object.setPrototypeOf(this, AgentApiError.prototype);
  }
}

/**
 * Fetch agents from the backend
 */
export async function fetchAgents(): Promise<Agent[]> {
  try {
    const response = await apiClient.get<AgentsResponse>('/agents');

    // Validate response
    if (!response.agents || !Array.isArray(response.agents)) {
      throw new AgentApiError(
        'Invalid response format: expected agents array',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    // Validate agent objects
    response.agents.forEach((agent, index) => {
      if (!agent.id || !agent.name || !agent.display_name) {
        throw new AgentApiError(
          `Invalid agent at index ${index}: missing required fields`,
          undefined,
          'INVALID_AGENT'
        );
      }
    });

    const showPreviewFeatures = ShowPreviewFeatures === 'true';
    // Finder visibility follows the backend-authorised catalogue, independently of previews.
    return showPreviewFeatures
      ? response.agents
      : response.agents.filter(a => a.id !== 'm365');
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new AgentApiError(
      `Failed to fetch agents: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'FETCH_FAILED'
    );
  }
}

/**
 * Pin an agent for the current user
 */
export async function pinAgent(agentId: string): Promise<AgentPinResponse> {
  try {
    if (!agentId) {
      throw new AgentApiError(
        'Agent ID is required',
        400,
        'MISSING_AGENT_ID'
      );
    }

    const response = await apiClient.post<AgentPinResponse>(`/agents/${agentId}/pin`);

    // Validate response
    if (!response.success || !response.agent_id) {
      throw new AgentApiError(
        'Invalid response format: missing success or agent_id',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new AgentApiError(
      `Failed to pin agent: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'PIN_FAILED'
    );
  }
}

/**
 * Unpin an agent for the current user
 */
export async function unpinAgent(agentId: string): Promise<AgentPinResponse> {
  try {
    if (!agentId) {
      throw new AgentApiError(
        'Agent ID is required',
        400,
        'MISSING_AGENT_ID'
      );
    }

    const response = await apiClient.delete<AgentPinResponse>(`/agents/${agentId}/pin`);

    // Validate response
    if (!response.success || !response.agent_id) {
      throw new AgentApiError(
        'Invalid response format: missing success or agent_id',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new AgentApiError(
      `Failed to unpin agent: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'UNPIN_FAILED'
    );
  }
}

/**
 * Query key factory for agents
 * Provides consistent cache keys for TanStack Query
 */
export const agentQueryKeys = {
  all: ['agents'] as const,
  lists: () => [...agentQueryKeys.all, 'list'] as const,
  list: (filters?: string) => [...agentQueryKeys.lists(), { filters }] as const,
  details: () => [...agentQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentQueryKeys.details(), id] as const,
  pins: () => [...agentQueryKeys.all, 'pin'] as const,
  pin: (id: string) => [...agentQueryKeys.pins(), id] as const,
};

/**
 * TanStack Query options for agents
 * Provides reusable, type-safe query configuration
 */
export const agentsQueryOptions = () =>
  queryOptions({
    queryKey: agentQueryKeys.lists(),
    queryFn: fetchAgents,
    staleTime: 5 * 60 * 1000, // 5 minutes - agents don't change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount: number, error: any) => {
      // Don't retry authentication errors
      if (error instanceof ApiError && error.status === 401) {
        return false;
      }

      // Don't retry client errors (4xx)
      if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
        return false;
      }

      // Retry network errors and server errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false, // No polling - use event-driven invalidation
    refetchIntervalInBackground: false,
  });

/**
 * Hook to fetch agents
 */
export const useAgents = () => {
  return useQuery(agentsQueryOptions());
};

/**
 * Hook to pin an agent
 */
export const useAgentPin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: pinAgent,
    // Optimistic update
    onMutate: async (agentId: string) => {
      await queryClient.cancelQueries({ queryKey: agentQueryKeys.lists() });

      const previousAgents = queryClient.getQueryData<Agent[]>(agentQueryKeys.lists());

      if (previousAgents) {
        const updated = previousAgents.map(a => a.id === agentId ? { ...a, pinned: true } : a);
        queryClient.setQueryData(agentQueryKeys.lists(), updated);
      }

      return { previousAgents } as { previousAgents?: Agent[] };
    },
    onError: (error, _agentId, context) => {
      console.error('Failed to pin agent:', error);
      if (context?.previousAgents) {
        queryClient.setQueryData(agentQueryKeys.lists(), context.previousAgents);
      }
    },
    onSuccess: (_data, agentId) => {
      // Keep modal state consistent if it's showing this agent
      const { selectedAgent } = useAgentsStore.getState();
      if (selectedAgent && selectedAgent.id === agentId && !selectedAgent.pinned) {
        useAgentsStore.setState({ selectedAgent: { ...selectedAgent, pinned: true } });
      }
    },
    onSettled: () => {
      // Confirm with server while preserving optimistic UX
      queryClient.invalidateQueries({ queryKey: agentQueryKeys.lists() });
    },
  });
};

/**
 * Hook to unpin an agent
 */
export const useAgentUnpin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unpinAgent,
    // Optimistic update
    onMutate: async (agentId: string) => {
      await queryClient.cancelQueries({ queryKey: agentQueryKeys.lists() });

      const previousAgents = queryClient.getQueryData<Agent[]>(agentQueryKeys.lists());

      if (previousAgents) {
        const updated = previousAgents.map(a => a.id === agentId ? { ...a, pinned: false } : a);
        queryClient.setQueryData(agentQueryKeys.lists(), updated);
      }

      return { previousAgents } as { previousAgents?: Agent[] };
    },
    onError: (error, _agentId, context) => {
      console.error('Failed to unpin agent:', error);
      if (context?.previousAgents) {
        queryClient.setQueryData(agentQueryKeys.lists(), context.previousAgents);
      }
    },
    onSuccess: (_data, agentId) => {
      // Keep modal state consistent if it's showing this agent
      const { selectedAgent } = useAgentsStore.getState();
      if (selectedAgent && selectedAgent.id === agentId && selectedAgent.pinned) {
        useAgentsStore.setState({ selectedAgent: { ...selectedAgent, pinned: false } });
      }
    },
    onSettled: () => {
      // Confirm with server while preserving optimistic UX
      queryClient.invalidateQueries({ queryKey: agentQueryKeys.lists() });
    },
  });
};

/**
 * Utility functions for agent data
 */
export const agentUtils = {
  /**
   * Get pinned agents from an array of agents
   */
  getPinnedAgents: (agents: Agent[]) => agents.filter(agent => agent.pinned),

  /**
   * Get recommended (non-pinned) agents from an array of agents
   */
  getRecommendedAgents: (agents: Agent[]) => agents.filter(agent => !agent.pinned),

  /**
   * Search agents by name and description
   */
  searchAgents: (agents: Agent[], query: string) => {
    if (!query.trim()) {
      return agents;
    }

    const lowerQuery = query.toLowerCase();
    return agents.filter(agent =>
      agent.name.toLowerCase().includes(lowerQuery) ||
      agent.description.toLowerCase().includes(lowerQuery)
    );
  },

  /**
   * Get display title for agent (truncated if needed)
   */
  getDisplayTitle: (agent: Agent, maxLength = 50): string => {
    const title = agent.name.trim();
    if (title.length <= maxLength) {
      return title;
    }
    return title.substring(0, maxLength - 3) + '...';
  },

  /**
   * Get display description for agent (truncated if needed)
   */
  getDisplayDescription: (agent: Agent, maxLength = 100): string => {
    const description = agent.description.trim();
    if (description.length <= maxLength) {
      return description;
    }
    return description.substring(0, maxLength - 3) + '...';
  },
};
